import { ClientReview, Selection, UserMap } from './types';

export const fakeUserMap: UserMap = {
  "1": "Bastian",
  "2": "Adrian", 
  "3": "Constanza",
  "4": "María José",
  "5": "Diego",
};

function makeSelection(itemId: number, name: string): Selection {
  return {
    quantity: 1,
    total_price: Math.round(Math.random() * 3000 + 1500),
    item_id: itemId,
    base_price: Math.round(Math.random() * 3000 + 1500),
    selection_id: crypto.randomUUID(),
    comment: '',
    item_name: name,
    clientIndex: 0,
    modifiers: [],
    selection_groups:
      Math.random() < 0.5
        ? [
            {
              selection_group_id: 1,
              selected_items: [
                { item_id: itemId * 10 + 1, quantity: 1, item_name: `${name} Extra Shot` },
              ],
            },
            {
              selection_group_id: 2,
              selected_items: [],
            },
          ]
        : [],
  };
}

const MONTHS = [
  "2024-12",
  "2025-01",
  "2025-02", 
  "2025-03",
  "2025-04",
  "2025-05",
  "2025-06",
  "2025-07",
  "2025-08",
];

const positiveComments = {
  amabilidad: [
    'Servicio impecable, muy atentos y amables.',
    'Me hicieron sentir como en casa.',
    'Excelente atención de principio a fin.',
    'El personal es súper profesional.',
    'Atención personalizada excepcional.',
    'Muy atentos a cada detalle.',
    'Servicio rápido y eficiente.',
    'El mesero fue muy amable.',
  ],
  sabor: [
    '¡Delicioso! Sabores perfectamente equilibrados.',
    'Una experiencia de sabor increíble.',
    'Cada bocado fue mejor que el anterior.',
    'Sabores auténticos y frescos.',
    'La calidad de los ingredientes se nota.',
    'Preparación perfecta, muy sabroso.',
    'Combinación de sabores exquisita.',
    'El mejor sabor que he probado.',
  ],
  experiencia: [
    'Ambiente fantástico y muy cómodo.',
    'La experiencia fue memorable.',
    'Todo fluyó a la perfección, ¡volveré pronto!',
    'Ambiente acogedor y relajante.',
    'Perfecto para compartir en familia.',
    'Experiencia gastronómica excepcional.',
    'Lugar ideal para una cita.',
    'Ambiente musical muy agradable.',
  ],
};

const neutralComments = {
  amabilidad: [
    'Servicio normal, sin problemas.',
    'Atención correcta.',
    'Todo bien en general.',
  ],
  sabor: [
    'Sabor decente, nada extraordinario.',
    'Está bien para el precio.',
    'Cumple las expectativas.',
  ],
  experiencia: [
    'Experiencia estándar.',
    'Lugar tranquilo.',
    'Sin quejas.',
  ],
};

// Enhanced dish names for more variety
const dishNames = [
  'Cappelletti', 'Lasagne Individual', 'Gnocchi Patate', 'Strozzapreti', 
  'Jugo de Mango', 'Risotto ai Funghi', 'Ravioli Spinaci', 'Fettuccine Alfredo',
  'Osso Buco', 'Saltimbocca', 'Tiramisu', 'Panna Cotta', 'Bruschetta',
  'Minestrone', 'Carbonara', 'Amatriciana', 'Pesto Genovese', 'Gelato',
  'Café Americano', 'Espresso', 'Macchiato', 'Té Verde', 'Limonada'
];

// Function to generate tail-distributed ratings (weighted towards 4-5)
function getTailDistributedRating(): number {
  const rand = Math.random();
  if (rand < 0.5) return 5;      // 50% chance of 5 stars
  if (rand < 0.75) return 4;     // 25% chance of 4 stars  
  if (rand < 0.9) return 3;      // 15% chance of 3 stars
  if (rand < 0.97) return 2;     // 7% chance of 2 stars
  return 1;                      // 3% chance of 1 star
}

export const fakeReviews: ClientReview[] = Array.from({ length: 75 }, (_, i) => {
  const receiptId = i + 1;
  const userId = [1, 2, 3, 4, 5][i % 5]; // Added more users for variety
  const pedido_type_id = [1,1,1,1,2,1,1,1,1,2,1,3,1,1,3,1,1,1,2,1,1,1,4,1,1][i % 25];

  // Use tail-distributed ratings (weighted towards good reviews)
  const amRating = getTailDistributedRating();
  const saRating = getTailDistributedRating();  
  const exRating = getTailDistributedRating();

  const hasFavorite = saRating >= 4;
  const hasWorst = saRating <= 2;

  // More varied timestamps across recent months
  const ym = MONTHS[i % MONTHS.length];
  const day = String(Math.ceil(Math.random() * 28)).padStart(2, '0');
  const hour = String(8 + Math.floor(Math.random() * 13)).padStart(2, '0'); // 8am to 9pm
  const minute = String(Math.floor(Math.random() * 60)).padStart(2, '0');
  const timestamp = `${ym}-${day} ${hour}:${minute}:00-04:00`;

  // Get comments based on rating
  const getComment = (rating: number, category: 'amabilidad' | 'sabor' | 'experiencia') => {
    if (rating >= 4) {
      return positiveComments[category][i % positiveComments[category].length];
    } else if (rating === 3) {
      return neutralComments[category][i % neutralComments[category].length];
    }
    return ''; // No comment for low ratings
  };

  return {
    local_id: 1,
    user_id: userId,
    receipt_id: receiptId,
    client_id: [null, null, 42, null, 99, 123, null, 456][i % 8] ?? null,
    pedido_type_id,
    timestamp,
    object: {
      amabilidad: {
        rating: amRating,
        comment: getComment(amRating, 'amabilidad'),
      },
      sabor: {
        rating: saRating,
        comment: getComment(saRating, 'sabor'),
        ...(hasFavorite && {
          favorite: makeSelection(
            10 + (i % dishNames.length),
            dishNames[i % dishNames.length]
          ),
        }),
        ...(hasWorst && {
          worst: makeSelection(
            50 + (i % dishNames.length),
            dishNames[i % dishNames.length]
          ),
        }),
      },
      experiencia: {
        rating: exRating,
        comment: getComment(exRating, 'experiencia'),
      },
    },
  };
});
